import React, { RefObject, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { tabs, tab, tabPanel } from '@harmony/enablers/react';
import scope from './providers/harmony-scope/provider-harmony-scope';
import PerformanceObserver from './services/performance-observer/service-performance-observer';
import { FilterPanel as FilterPanelv2 } from './features/panel-filters/component-filters-panel';
import { FilterPills } from './features/panel-filters/component-filter-pills';
import { TrackEngagement } from './services/logging/component-track-engagement';
import { useBuildICMProperties, useTrackICMEvent, useTrackInteraction } from './services/logging/hooks';
import {
  addError,
  addNotification,
  clearBannerMessages,
  clearBannerMessagesByType,
  EBannerAppearance,
  EBannerType,
} from './ducks/banner-messages/duck-banner-messages';
import { useAppSelector, useAppDispatch } from './ducks/hooks';
import { endImpersonation } from './ducks/impersonation/duck-impersonation';
import { PageContext } from './utils/page-context';
import { useTabConfig } from './services/tab-configurations/tab-configurations';
import { Button, ProgressRing, CommandBar, Icon, MessageBar, Option } from './providers/harmony-scope/lazy-enablers';
import { BannerMessagesContainer } from './shared-components/banner-messages-container';
import { EUserRole } from './ducks/api-ducks/participation-details-duck/types-participation-details';
import { ETabId } from './services/tab-configurations/tab-types';
import { setActiveTabFilter } from './ducks/filters/duck-filters';
import { bodyContentStyles, titleCommandBarStyles } from './utils/common-styles';
import { useLCPMarker, useTTIMarker } from './services/performance-observer/hooks';
import { useGetUserNotificationsQuery } from './ducks/api-ducks/user-notifications-duck/api-duck-user-notifications';
import { EICMSeverity, EICMType } from './enums/icm-enum';
import { Flags, useFlagValue } from './services/feature-flags/hooks';
import SellerExperienceHeader from './shared-components/seller-experience-header';
import { EServiceName } from './ducks/api-ducks/types-shared';
import { EUserNotificationStatus, EUserNotificationType } from './ducks/api-ducks/user-notifications-duck/types-user-notifications';
import { StandaloneLayout } from './features/header-standalone/header-standalone';
import { useMEMTranslation } from './utils/text-utils';
import { ButtonWithBadgeWithCount } from './shared-components/badge-with-count';
import { NotificationsPanel } from './features/panel-notifications/component-notifications-panel';
import { DownloadsPanel } from './features/panel-downloads/component-downloads-panel';
import { closeDownloadPanel } from './ducks/download-panel/download-panel';
import { Flight } from './services/feature-flags/component-flight';
import { useDefaultTab, useGetNotifciationRefreshTime, useShowFilters } from './seller-experience-hooks';

// We don't lazy load the tabs and text component since they affect the TTI metric.
const Tabs = scope.forReact(tabs);
const Tab = scope.forReact(tab);
const TabPanel = scope.forReact(tabPanel);

const filtersPanelTitle = 'FiltersPanel';
const notificationsPanelTitle = 'NotificationsPanel';
const downloads = 'downloads';
const pageName = 'Seller Experience';

const tabPanelStyle = {
  marginTop: '1.5rem',
};

const SellerExperience = () => {
  const { t } = useMEMTranslation();
  const tabsRef = useRef(undefined) as unknown as RefObject<tabs | null>;
  const [filtersPanelOpen, setFiltersPanelOpen] = useState<boolean>(false);
  // Removed filterButtonRef, fallback focus logic will use getElementById only
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState<boolean>(false);
  const [downloadPanelOpen, setDownloadPanelOpen] = useState<boolean>(false);
  const [filtersAppliedMsg, setFiltersAppliedMsg] = useState<string | null>(null);
  const trackInteraction = useTrackInteraction();
  const filters = useAppSelector((state) => state.filters);
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const impersonation = useAppSelector((state) => state.impersonation);
  const appConfig = useAppSelector((state) => state.appConfig);
  // Select download manager state from redux
  const downloadsState = useAppSelector((state) => state.downloadManager);
  const dispatch: any = useAppDispatch();
  const tabConfig = useTabConfig();
  const availableTabs = useMemo(() => tabConfig.filter((tabItem) => tabItem.show), [tabConfig]);
  const defaultTabId = useDefaultTab(availableTabs);
  const [activeTab, setActiveTab] = useState<ETabId>(defaultTabId || availableTabs[0]?.tabId);
  const acFlagEnabled = useFlagValue(Flags.AC_BANNER_NOTIFICATIONS);
  const showFilters = useShowFilters();
  const downloadFlagEnabled = useFlagValue(Flags.DOWNLOAD_FLAG) || (impersonation?.isImpersonating && appConfig?.isBusinessPersona);
  const trackICMEvent = useTrackICMEvent();
  const notificationRefreshTime = useGetNotifciationRefreshTime();
  const buildICMProperties = useBuildICMProperties();
  const fetchACNotifications = useMemo(() => {
    const isBusinessImpersonation = impersonation.isImpersonating && appConfig.isBusinessPersona;
    const isManagerImpersonation = impersonation.isImpersonating && !appConfig.isBusinessPersona;
    const isSellerTerminated = participationDetails?.personnelNumber === Number(participationDetails?.alias);

    return (
      // We check for either the flag or a business impersonation
      (acFlagEnabled || isBusinessImpersonation) &&
      // We then verify that its not a managerImpersonation happening
      !isManagerImpersonation &&
      // If its an impersonation we wait for participant details
      (participationDetails || !isBusinessImpersonation) && // finally we verify that the seller is not terminated
      !isSellerTerminated
    );
  }, [acFlagEnabled, appConfig.isBusinessPersona, impersonation.isImpersonating, participationDetails]);

  const {
    data: userNotificationsData,
    isError: userNotificationsIsError,
    error: userNotificationsError,
  } = useGetUserNotificationsQuery(
    fetchACNotifications
      ? {
          cacheKey: impersonation.impersonatedUser, // Caching key to re-fetch after different impersonations.
          notificationStatus: EUserNotificationStatus.Active, // We fetch only the Active notifications. Expired ones are fetched on demand.
        }
      : skipToken,
    {
      pollingInterval: notificationRefreshTime,
      skipPollingIfUnfocused: true,
    }
  );

  const newNotificationsCount = useMemo(
    () => userNotificationsData?.notifications?.filter((notification) => notification.notificationStatus === EUserNotificationStatus.Active).length,
    [userNotificationsData]
  );

  const planDisabledErrorLogged = useMemo(() => {
    if (filters && participationDetails) {
      const selectedParticipation = participationDetails.coreParticipations.find(
        (participation) => filters.participationId === participation.participationId
      );
      if (selectedParticipation?.isPlanActive) {
        return false;
      }
      if (selectedParticipation?.userRole === EUserRole.NonParticipantManager) {
        return false;
      }
      if (impersonation.isImpersonating) {
        return false;
      }
      // We show the Error whenever the plan is not active or the revenue release is not enabled, its not an NPM and its not impersonating
      return true;
    }
    return false;
  }, [filters, impersonation.isImpersonating, participationDetails]);

  useEffect(() => {
    if (planDisabledErrorLogged) {
      dispatch(
        addError({
          message: t('errorPlanRevenueDisabled'),
          bannerType: EBannerType.PageError,
        })
      );
    }
  }, [dispatch, planDisabledErrorLogged, t]);

  // Get Notifications for user
  useEffect(() => {
    if (userNotificationsIsError) {
      const icmProps = buildICMProperties(EICMType.API, EICMSeverity.Sev4, EServiceName.NOTIFICATION, userNotificationsError);
      trackICMEvent('GetUserNotifications API failed all retries', pageName, '', icmProps);
    }
    if (userNotificationsData && userNotificationsData.notifications && userNotificationsData.notifications.length > 0) {
      userNotificationsData.notifications.forEach((notification) => {
        if (notification.notificationType === EUserNotificationType.ActionCenter || notification.notificationType === EUserNotificationType.Banner) {
          dispatch(
            addNotification({
              notification,
              bannerType: EBannerType.UserNotification,
              appearance: EBannerAppearance.Warning,
            })
          );
        }
      });
    }
  }, [buildICMProperties, dispatch, trackICMEvent, userNotificationsData, userNotificationsError, userNotificationsIsError]);

  // If Available tabs are updated, then set the activeTab to the default tab, if the current tab is not available.
  useEffect(() => {
    if (availableTabs.length > 0 && defaultTabId) {
      dispatch(setActiveTabFilter(defaultTabId));
      setActiveTab(defaultTabId);
    }
  }, [availableTabs, defaultTabId, dispatch, impersonation.impersonatedUser]);

  // Clear banner messages on impesonation change
  useEffect(() => {
    dispatch(clearBannerMessages());
  }, [impersonation.impersonatedUser, dispatch]);

  // Clear errors whenever a tab change is detected on filters
  useEffect(() => {
    dispatch(clearBannerMessagesByType(EBannerType.PageError));
  }, [dispatch, filters.activeTab]);

  // Open the downloads panel if there is any download of type 'direct' or 'queue' with status 'BEGIN'
  useEffect(() => {
    if (!downloadsState) return;
    // Check direct downloads
    const openPanel = downloadsState.panelOpen;

    if (openPanel) {
      setDownloadPanelOpen(true);
      dispatch(closeDownloadPanel());
    }
  }, [downloadsState, dispatch]);

  const openFilterPanel = () => {
    trackInteraction('Opened filter panel', pageName, 'Filter Panel');
    setFiltersPanelOpen(true);
  };

  const openDownloadsPanel = () => {
    trackInteraction('Opened downloads panel', pageName, 'Downloads Panel');
    setDownloadPanelOpen(true);
  };
  // Focus back to filter button when panel closes, using getElementById fallback only
  useEffect(() => {
    if (!filtersPanelOpen) {
      const fallbackBtn = document.getElementById('OpenFilterPanelButton');
      if (fallbackBtn && typeof fallbackBtn.focus === 'function') {
        fallbackBtn.focus();
      }
    }
  }, [filtersPanelOpen]);

  const openNotificationsPanel = () => {
    trackInteraction('Opened notifications panel', pageName, 'Notifications Panel');
    setNotificationsPanelOpen(true);
  };

  const afterImpersonationBannerHide = () => {
    dispatch(endImpersonation());
    dispatch(clearBannerMessages());
  };

  const tabChanged = (event: CustomEvent<{ activeTab: HTMLElement }>) => {
    if (event.detail.activeTab && event.detail.activeTab.id) {
      // We stop the web performance observer when the tab changes, since an interaction has happened.
      if (PerformanceObserver) {
        PerformanceObserver.cleanup();
      }

      const currTab = event.detail.activeTab.id as ETabId;
      if (availableTabs.map((tTab) => tTab.tabId).includes(currTab)) {
        if (activeTab !== currTab) {
          dispatch(setActiveTabFilter(currTab));
          setActiveTab(currTab);
          trackInteraction('Tab Opened', pageName, currTab);
        }
      }
    }
  };

  // LCP Performance Mark
  useLCPMarker();
  useTTIMarker(availableTabs.length > 0);
  return (
    <StandaloneLayout>
      <div
        style={{
          position: 'relative',
          minHeight: '100vh',
        }}
      >
        <div style={{ ...bodyContentStyles, userSelect: 'text' }}>
          <div style={titleCommandBarStyles}>
            <SellerExperienceHeader />
            <CommandBar style={{ width: 'auto', flexGrow: 1 }} actionsPosition="end">
              {showFilters && (
                <Button id="OpenFilterPanelButton" appearance="command" onClick={openFilterPanel}>
                  <Icon slot="start" name="filter" label="filters" />
                  {t('sellerExperienceFilterButton')}
                </Button>
              )}
              <Flight flag={Flags.NOTIFICATIONS_BELL}>
                <ButtonWithBadgeWithCount
                  contentNumber={newNotificationsCount}
                  hide={!newNotificationsCount || newNotificationsCount === 0}
                  id="OpenNotificationsPanel"
                  appearance="command"
                  onClick={openNotificationsPanel}
                >
                  <Icon name="ringer" label={`${t('sellerExperienceBellIconAriaLabel')}: ${newNotificationsCount}`} />
                </ButtonWithBadgeWithCount>
              </Flight>
              {showFilters && downloadFlagEnabled && (
                <Button id="OpenPanelButton" appearance="command" onClick={openDownloadsPanel}>
                  <Icon name="download" />
                </Button>
              )}
            </CommandBar>
          </div>
          {impersonation.isImpersonating && (
            <MessageBar appearance="info" open onHeMessageBarAfterHide={afterImpersonationBannerHide}>
              {`${t('sellerExperienceImpersonatingString')} ${impersonation.impersonatedUser}`}
            </MessageBar>
          )}
          <BannerMessagesContainer />
          <div style={{ paddingBottom: '1rem' }} />
          {showFilters && <FilterPills />}
        </div>
        <div style={{ ...bodyContentStyles, userSelect: 'text' }}>
          <Tabs orientation="horizontal" size="large" onHeChange={tabChanged} active-id={activeTab} ref={tabsRef} manualActivation>
            {availableTabs.map((tabItem) => (
              <React.Fragment key={`${tabItem.tabId}-tabKey`}>
                <Tab id={tabItem.tabId}>{tabItem.tabName}</Tab>
                <Suspense fallback={<ProgressRing indeterminate style={{ margin: '50%' }} />}>
                  <TabPanel style={tabPanelStyle}>
                    {activeTab === tabItem.tabId && (
                      <PageContext.Provider value={tabItem.tabId}>
                        <TrackEngagement componentName={tabItem.tabId}>{tabItem.component}</TrackEngagement>
                      </PageContext.Provider>
                    )}
                  </TabPanel>
                </Suspense>
              </React.Fragment>
            ))}
          </Tabs>
        </div>
        {/* This is a hack to pre-fetch the lazy loaded Option component so that it doesnt show blank comboboxes */}
        <Option hidden aria-hidden />
      </div>
      {filters && filtersPanelOpen && (
        <TrackEngagement componentName={filtersPanelTitle}>
          <FilterPanelv2
            closePanel={(applied?: boolean) => {
              if (applied) {
                setFiltersAppliedMsg(t('ariaFiltersAppliedSuccess'));
                setTimeout(() => {
                  setFiltersPanelOpen(false);
                  setTimeout(() => setFiltersAppliedMsg(null), 3000);
                }, 50);
              } else {
                setFiltersPanelOpen(false);
              }
            }}
          />
        </TrackEngagement>
      )}
      {notificationsPanelOpen && (
        <Flight flag={Flags.NOTIFICATIONS_BELL}>
          <TrackEngagement componentName={notificationsPanelTitle}>
            <NotificationsPanel closePanel={() => setNotificationsPanelOpen(false)} />
          </TrackEngagement>
        </Flight>
      )}
      {filters && downloadPanelOpen && downloadFlagEnabled && (
        <TrackEngagement componentName={downloads}>
          <DownloadsPanel closePanel={() => setDownloadPanelOpen(false)} />
        </TrackEngagement>
      )}
      {/* ARIA live region for filter applied message - moved to end for priority */}
      <div aria-live="assertive" aria-atomic="true" style={{ position: 'absolute', left: -9999, height: 1, width: 1, overflow: 'hidden' }}>
        {filtersAppliedMsg}
      </div>
    </StandaloneLayout>
  );
};

export default SellerExperience;
